import React from 'react';
import {hasFeatureFlag} from '@/forecast-app/shared/util/FeatureUtil';
import {useIntl} from 'react-intl';
import {BudgetHeaderCard, FinancialNumber} from './ProgramBudgetTotals';
import {DeprecatedText as Text} from '@forecast-it/design-system';
import {NUMBER_TYPE} from '@/components/new-ui/project/project-budget-v3/util/BudgetUtils';
import {PROGRAM_BUDGET_TYPE} from '@/constants';
import {ProgramHeaderTooltips as HeaderTooltips} from './ProgramHeaderTooltips';

const ProfitSection = ({program}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: hasFinancialCategoriesFeature ? 'common.profit_margin' : 'common.profit'})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'project_budget.actual_margin' : 'project_budget.profit_margin_to_date'
				}
				cy={'profitMargin.toDate'}
				tooltip={'ProfitMarginActualToDate'}
				tooltipMaxWidth={1000}
				value={program.projectsFinancialsToDate.recognitionActualProfitPercentage}
				numberType={NUMBER_TYPE.PERCENTAGE}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualMarginTooltip}
			/>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_margin'
						: 'project_budget.profit_margin_at_completion'
				}
				cy={'profitMargin.atCompletion'}
				tooltip={'ProfitMarginAtCompletion'}
				tooltipMaxWidth={1000}
				value={program.projectsFinancialsTotals.recognitionProfitPercentage}
				numberType={NUMBER_TYPE.PERCENTAGE}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalMarginTooltip}
			/>
		</BudgetHeaderCard>
	);
};

const ActualRevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature ? 'project_budget.actual_revenue' : 'project_budget.revenue_to_date',
				})}
			</Text>
			<FinancialNumber
				translationId={hasFinancialCategoriesFeature ? 'project_budget.actual_revenue' : 'common.recognition_to_date'}
				cy={'revenueRecognition.toDate'}
				tooltip={'RevenueRecognizedToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualRevenueTooltip}
			/>
			<FinancialNumber
				translationId={'program_budget.variance_with_cap'}
				cy={'revenueRecognition.toDate.variance'}
				tooltip={'RevenueRecognizedToDateCapVariance'}
				tooltipMaxWidth={450}
				value={program.budgetValue - program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.VarianceWithCapActualRevenueTooltip}
			/>
		</BudgetHeaderCard>
	);
};

const ProjectedTotalRevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature
						? 'common.projected_total_revenue'
						: 'project_budget.revenue_at_completion',
				})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_revenue'
						: 'project_budget.recognition_at_completion'
				}
				cy={'revenueRecognition.atCompletion'}
				tooltip={'RevenueRecognitionAtCompletion'}
				tooltipMaxWidth={600}
				value={program.projectsFinancialsTotals.recognitionForecastRevenue}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalRevenueTooltip}
			/>
			<FinancialNumber
				translationId={'program_budget.variance_with_cap'}
				cy={'revenueRecognition.atCompletion.variance'}
				tooltip={'RevenueRecognitionAtCompletionCapVariance'}
				tooltipMaxWidth={450}
				value={program.budgetValue - program.projectsFinancialsTotals.recognitionForecastRevenue}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.VarianceWithCapProjectedTotalRevenueTooltip}
			/>
		</BudgetHeaderCard>
	);
};
const RevenueSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');
	const isFixedPrice = program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE;

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'project_budget.revenue'})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'project_budget.actual_revenue' : 'common.revenue_recognition_to_date'
				}
				cy={'revenueRecognition.toDate'}
				tooltip={'RevenueRecognizedToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsToDate.totalActualRevenueRecognition}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualRevenueTooltip}
			/>
			{isFixedPrice ? (
				<FinancialNumber
					translationId={
						hasFinancialCategoriesFeature ? 'common.remaining_budget' : 'project_budget.remaining_revenue'
					}
					cy={'revenueRecognition.remaining'}
					tooltip={'RemainingRevenue'}
					tooltipMaxWidth={350}
					value={program.budgetValue - program.projectsFinancialsToDate.totalActualRevenueRecognition}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.RemainingBudgetTooltip}
				/>
			) : (
				<FinancialNumber
					translationId={
						hasFinancialCategoriesFeature
							? 'common.projected_total_revenue'
							: 'common.revenue_recognition_at_completion'
					}
					cy={'revenueRecognition.atCompletion'}
					tooltip={'RevenueRecognitionAtCompletion'}
					tooltipMaxWidth={350}
					value={program.projectsFinancialsTotals.recognitionForecastRevenue}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.ProjectedTotalRevenueTooltip}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ActualValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature
						? 'common.actual_value_of_service'
						: 'project_budget.value_of_service_to_date',
				})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'common.actual_value_of_service' : 'project_budget.value_of_service_to_date'
				}
				cy={'valueOfService.toDate'}
				tooltip={'SpendActualToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualValueOfServiceTooltip}
			/>
			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE ? (
				<FinancialNumber
					translationId={'program_budget.variance_with_fixed_price'}
					cy={'valueOfService.toDate.variance'}
					tooltip={'SpendActualToDateFixedPriceVariance'}
					tooltipMaxWidth={450}
					value={program.budgetValue - program.projectsFinancialsTotals.allActualTimeAndExpenses}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithFixedPriceActualValueOfServiceTooltip}
				/>
			) : (
				<FinancialNumber
					translationId={'program_budget.variance_with_cap'}
					cy={'valueOfService.toDate.variance'}
					tooltip={'SpendActualToDateCapVariance'}
					tooltipMaxWidth={450}
					value={program.budgetValue - program.projectsFinancialsTotals.allActualTimeAndExpenses}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithCapActualValueOfServiceTooltip}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ProjectedTotalValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({
					id: hasFinancialCategoriesFeature
						? 'common.projected_total_value_of_service'
						: 'project_budget.value_of_service_at_completion',
				})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_value_of_service'
						: 'project_budget.value_of_service_at_completion'
				}
				cy={'valueOfService.atCompletion'}
				tooltip={'SpendTotalAtCompletion'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalValueOfServiceTooltip}
			/>
			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE ? (
				<FinancialNumber
					translationId={'program_budget.variance_with_fixed_price'}
					cy={'valueOfService.atCompletion.variance'}
					tooltip={'SpendTotalAtCompletionFixedPriceVariance'}
					tooltipMaxWidth={350}
					value={program.budgetValue - program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithFixedPriceProjectedTotalValueOfServiceTooltip}
				/>
			) : (
				<FinancialNumber
					translationId={'program_budget.variance_with_cap'}
					cy={'valueOfService.atCompletion.variance'}
					tooltip={'SpendTotalAtCompletionCapVariance'}
					tooltipMaxWidth={350}
					value={program.budgetValue - program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
					numberType={NUMBER_TYPE.MONEY}
					currencySymbol={currencySymbol}
					size={'s'}
					tooltipComponent={HeaderTooltips.VarianceWithCapProjectedTotalValueOfServiceTooltip}
				/>
			)}
		</BudgetHeaderCard>
	);
};

const ValueOfServiceSection = ({program, currencySymbol}) => {
	const {formatMessage} = useIntl();
	const hasFinancialCategoriesFeature = hasFeatureFlag('financial_categories_update');

	return (
		<BudgetHeaderCard>
			<Text type={'heading'} variant={'m'} color={'primary'}>
				{formatMessage({id: 'common.value_of_service'})}
			</Text>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature ? 'common.actual_value_of_service' : 'project_budget.value_of_service_to_date'
				}
				cy={'valueOfService.toDate'}
				tooltip={'SpendActualToDate'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ActualValueOfServiceTooltip}
			/>
			<FinancialNumber
				translationId={
					hasFinancialCategoriesFeature
						? 'common.projected_total_value_of_service'
						: 'project_budget.value_of_service_at_completion'
				}
				cy={'valueOfService.atCompletion'}
				tooltip={'SpendTotalAtCompletion'}
				tooltipMaxWidth={350}
				value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
				numberType={NUMBER_TYPE.MONEY}
				currencySymbol={currencySymbol}
				size={'s'}
				tooltipComponent={HeaderTooltips.ProjectedTotalValueOfServiceTooltip}
			/>
		</BudgetHeaderCard>
	);
};

export const ProgramHeaderSections = {
	ProfitSection,
	ActualRevenueSection,
	ProjectedTotalRevenueSection,
	RevenueSection,
	ActualValueOfServiceSection,
	ProjectedTotalValueOfServiceSection,
	ValueOfServiceSection,
};
